import _ from 'underscore';
import { useCallback } from 'react';
import type {
  DataRefId,
  EntityObject,
  OverreactRequestShape,
  OverreactSpec,
  RequestConfig,
  RequestOptions,
} from '../public-types';
import { useEnvironment } from '../environment';
import {
  getDataRef, getRecords, updateDataRefWithIds,
} from '../store';
import type { Store } from '../store';
import type { RecordGroup } from '../store/record-group';
import { useDeepMemo } from '../utils/primitive-utilities';
import { getDataFromRecords, getLookupCacheFn } from './helper';
import type { LookupCacheFn } from './helper';
import { getCacheIds } from './lookup-cache';
import { getMergedConfig } from './merge-config';
import type { ConfigBuilder } from './merge-config';
import { OverreactRequest } from './overreact-request';
import { useComponent } from './use-component';

interface RefetchVariables {
  options?: RequestOptions;
}

export interface RefetchConfig<
  TEntity extends EntityObject,
  TVariables extends object,
> extends RequestConfig<TEntity | TEntity[]> {
  lookupCacheByVariables?: LookupCacheFn<TEntity, TVariables>;
  fetchFromStoreFn?: (
    _recordGroup: RecordGroup<TEntity>,
    _variables: TVariables,
  ) => string[];
}

export interface RefetchParameters<TVariables extends object, TPayload> {
  variables: TVariables;
  payload?: TPayload;
}

export function useRefetch<
  TEntity extends EntityObject,
  TVariables extends RefetchVariables,
  TPayload = unknown,
>(
  dataRefId: DataRefId,
  spec: OverreactSpec<TVariables, TPayload, TEntity | TEntity[], TEntity>,
  config?: RefetchConfig<TEntity, TVariables>,
) {
  const componentName = useComponent();
  const memoedConfig = useDeepMemo(config) || {};
  const { requestContract, responseContract, environmentLookupFn } = spec;
  const environment = useEnvironment(environmentLookupFn);

  const dataCallback = useCallback((
    _dataItems: unknown,
    request: OverreactRequestShape<TVariables, TPayload, TEntity | TEntity[], TEntity>,
  ) => {
    if (!environment) {
      return;
    }
    const records = getRecords(
      environment.store as Store<TEntity>,
      requestContract,
      dataRefId,
    ) || [];
    const data = getDataFromRecords(records, responseContract);
    request.mergedConfig?.onComplete?.(data);
  }, [dataRefId, environment, requestContract, responseContract]);

  const errorCallback = useCallback((
    error: unknown,
    request: OverreactRequestShape<TVariables, TPayload, TEntity | TEntity[], TEntity>,
  ) => {
    request.mergedConfig?.onError?.(error);
  }, []);

  return useCallback((
    parameter: RefetchParameters<TVariables, TPayload>,
    ...rest: Array<
      Partial<RefetchConfig<TEntity, TVariables>>
      | ConfigBuilder<RefetchConfig<TEntity, TVariables>>
      | undefined
    >
  ) => {
    if (!environment) {
      return;
    }
    const requestConfig = rest.slice(-1)[0];
    const mergedConfig = getMergedConfig(requestConfig, memoedConfig);
    const { variables, payload } = parameter;
    const fetchPolicy = variables.options?.fetchPolicy || requestContract.fetchPolicy;
    const request = new OverreactRequest({
      id: dataRefId,
      requestContract,
      spec,
      variables,
      data: payload as TPayload,
      dataCb: dataCallback,
      errorCb: errorCallback,
      mergedConfig,
      componentName,
    });
    const lookupFn = getLookupCacheFn(
      mergedConfig.lookupCacheByVariables,
      spec,
      fetchPolicy,
    );
    if (lookupFn) {
      try {
        const store = environment.store as Store<TEntity>;
        const overreactIds = getCacheIds({
          store,
          requestContract,
          variables,
          lookupFn,
        });
        if (!_.isEmpty(overreactIds)) {
          const dataRef = getDataRef(store, requestContract, dataRefId);
          if (dataRef) {
            updateDataRefWithIds(dataRef, overreactIds);
          }
          const records = getRecords(store, requestContract, dataRefId) || [];
          dataCallback(getDataFromRecords(records, responseContract), request);
          return;
        }
      } catch (error) {
        // TODO: log error and send request
      }
    }
    environment.pushRequest(request);
  }, [
    componentName,
    dataCallback,
    dataRefId,
    environment,
    errorCallback,
    memoedConfig,
    requestContract,
    responseContract,
    spec,
  ]);
}
